import axios from 'axios';
import config from '../config';

const API_URL = config.apiUrl;

const api = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor to add auth token and company context
api.interceptors.request.use(
  (config) => {
    // Skip auth/company for public dashboard routes - they use their own token
    if (config.url?.includes('/public/')) {
      return config;
    }
    // Get token and company from persisted store
    const authData = localStorage.getItem('neoshift-auth');
    if (authData) {
      try {
        const { state } = JSON.parse(authData);
        if (state?.accessToken) {
          config.headers.Authorization = `Bearer ${state.accessToken}`;
        }
        
        // If there's a selected company, add company ID to requests
        // Backend will validate if user has permission to access this company
        const companyId = state?.selectedCompany?._id || state?.selectedCompany;
        if (companyId) {
          // Add to query params for GET requests, body for others
          if (config.method === 'get') {
            config.params = { ...config.params, companyId };
          } else if (config.data) {
            // For POST/PUT/PATCH with body
            // IMPORTANT: Don't spread FormData - it breaks file uploads
            if (config.data instanceof FormData) {
              // FormData already handles companyId - don't modify
              // The frontend should already append companyId to FormData
              // Also delete Content-Type so browser sets it with proper multipart boundary
              delete config.headers['Content-Type'];
            } else if (typeof config.data === 'string') {
              const data = JSON.parse(config.data);
              data.companyId = companyId;
              config.data = JSON.stringify(data);
            } else {
              config.data = { ...config.data, companyId };
            }
          }
        }
      } catch (e) {
        console.error('Error parsing auth data:', e);
      }
    }
    
    // Handle FormData even when no companyId in state (e.g., for public uploads)
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // If 401 and not already retrying
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      // Try to refresh token
      const authData = localStorage.getItem('neoshift-auth');
      if (authData) {
        try {
          const { state } = JSON.parse(authData);
          if (state?.refreshToken) {
            const response = await axios.post(`${API_URL}/auth/refresh-token`, {
              refreshToken: state.refreshToken
            });
            
            const { accessToken, refreshToken } = response.data;
            
            // Update stored tokens
            localStorage.setItem('neoshift-auth', JSON.stringify({
              state: { accessToken, refreshToken }
            }));
            
            // Retry original request
            originalRequest.headers.Authorization = `Bearer ${accessToken}`;
            return api(originalRequest);
          }
        } catch (refreshError) {
          // Clear auth data on refresh failure
          localStorage.removeItem('neoshift-auth');
          window.location.href = '/#/login';
        }
      }
    }
    
    return Promise.reject(error);
  }
);

export default api;

// Store original API URL for reference
const originalApiUrl = API_URL;

/**
 * Update API endpoint for regional routing
 * Fetches the company's regional endpoint and switches the API base URL if available
 * This optimizes BigQuery data transfer costs by routing to Cloud Run in the same region
 * @returns {string|null} The regional endpoint URL if switched, null otherwise
 */
export const updateApiEndpoint = async () => {
  try {
    // Get companyId from persisted store (for Super/Managing Admins who have selectedCompany)
    let companyId = null;
    let companyName = null;
    const authData = localStorage.getItem('neoshift-auth');
    if (authData) {
      try {
        const { state } = JSON.parse(authData);
        // Check selectedCompany first (for Super/Managing Admins), then fall back to company
        const company = state?.selectedCompany || state?.company;
        companyId = company?._id || company;
        companyName = company?.name;
      } catch (e) {
        // Ignore parse errors
      }
    }
    
    console.log(`[Regional Routing] Fetching endpoint for company: ${companyName || companyId || 'none'}`);
    console.log(`[Regional Routing] Current baseURL: ${api.defaults.baseURL}`);
    
    // Build URL with optional companyId query param
    const url = companyId ? `/companies/my-endpoint?companyId=${companyId}` : '/companies/my-endpoint';
    const response = await api.get(url);
    const { regionalRoutingEnabled, apiEndpoint, region, message } = response.data;
    
    console.log(`[Regional Routing] Response:`, { regionalRoutingEnabled, apiEndpoint, region, message });
    
    if (regionalRoutingEnabled && apiEndpoint) {
      console.log(`[Regional Routing] Switching to regional endpoint: ${apiEndpoint} (region: ${region})`);
      api.defaults.baseURL = apiEndpoint;
      // Store in session storage so it persists across page refreshes but not browser sessions
      sessionStorage.setItem('neoshift-regional-endpoint', apiEndpoint);
      return apiEndpoint;
    } else {
      console.log(`[Regional Routing] NOT switching - routing ${regionalRoutingEnabled ? 'enabled but no regional endpoint configured' : 'disabled'} for region: ${region}`);
    }
  } catch (error) {
    console.warn('[Regional Routing] Failed to fetch regional endpoint, using default', error);
  }
  return null;
};

/**
 * Reset API endpoint to default
 * Used when logging out to ensure next user gets fresh routing
 */
export const resetApiEndpoint = () => {
  api.defaults.baseURL = originalApiUrl;
  sessionStorage.removeItem('neoshift-regional-endpoint');
  console.log('[Regional Routing] Reset to default endpoint');
};

/**
 * Restore regional endpoint from session storage
 * Called on app initialization to restore previous session's routing
 */
export const restoreApiEndpoint = () => {
  const savedEndpoint = sessionStorage.getItem('neoshift-regional-endpoint');
  if (savedEndpoint) {
    api.defaults.baseURL = savedEndpoint;
    console.log(`[Regional Routing] Restored regional endpoint: ${savedEndpoint}`);
    return true;
  }
  return false;
};

// Helper functions for common API calls
export const apiHelpers = {
  // Dashboards
  getDashboards: (params) => api.get('/dashboards', { params }),
  getDashboard: (id) => api.get(`/dashboards/${id}`),
  getDashboardWithCharts: (id) => api.get(`/dashboards/${id}/charts`),
  getShareLinkLimits: () => api.get('/dashboards/share-links/limits'),
  getShareLinks: (dashboardId) => api.get(`/dashboards/${dashboardId}/share-links`),
  createShareLink: (dashboardId, data) => api.post(`/dashboards/${dashboardId}/share-links`, data),
  updateShareLink: (dashboardId, linkId, data) => api.put(`/dashboards/${dashboardId}/share-links/${linkId}`, data),
  deleteShareLink: (dashboardId, linkId) => api.delete(`/dashboards/${dashboardId}/share-links/${linkId}`),
  addShareLinkCredential: (dashboardId, linkId, data) => api.post(`/dashboards/${dashboardId}/share-links/${linkId}/credentials`, data),
  updateShareLinkCredential: (dashboardId, linkId, credId, data) => api.put(`/dashboards/${dashboardId}/share-links/${linkId}/credentials/${credId}`, data),
  deleteShareLinkCredential: (dashboardId, linkId, credId) => api.delete(`/dashboards/${dashboardId}/share-links/${linkId}/credentials/${credId}`),
  createDashboard: (data) => api.post('/dashboards', data),
  updateDashboard: (id, data) => api.put(`/dashboards/${id}`, data),
  deleteDashboard: (id) => api.delete(`/dashboards/${id}`),
  
  // Charts
  getCharts: (params) => api.get('/charts', { params }),
  getChart: (id) => api.get(`/charts/${id}`),
  getChartData: (id, filters) => api.get(`/charts/${id}/data`, { params: { filters: JSON.stringify(filters) }, timeout: 60000 }), // 60s timeout for complex queries
  getChartFilterOptions: (chartId, filterId) => api.get(`/charts/${chartId}/filter-options/${filterId}`),
  
  // Public dashboard (share links - token from login or public access)
  getPublicDashboardInfo: (slug) => api.get(`/public/dashboard/${slug}/info`),
  publicDashboardLogin: (slug, username, password) => api.post(`/public/dashboard/${slug}/login`, { username, password }),
  getPublicDashboard: (slug, token) => api.get(`/public/dashboard/${slug}`, token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
  getPublicChartData: (slug, chartId, token) => api.get(`/public/dashboard/${slug}/charts/${chartId}/data`, {
    ...(token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
    timeout: 60000
  }),
  getPublicChartFilterOptions: (slug, chartId, filterId, token) => api.get(`/public/dashboard/${slug}/charts/${chartId}/filter-options/${filterId}`, 
    token ? { headers: { Authorization: `Bearer ${token}` } } : {}
  ),
  previewChartQuery: (data) => api.post('/charts/preview', data),
  createChart: (data) => api.post('/charts', data),
  updateChart: (id, data) => api.put(`/charts/${id}`, data),
  deleteChart: (id) => api.delete(`/charts/${id}`),
  
  // Data Sources
  getDataSources: (params) => api.get('/data-sources', { params }),
  getChartSources: (params) => api.get('/data-sources/chart-sources', { params }),
  getBigQueryTableSchema: (params) => api.get('/data-sources/bigquery-schema', { params }),
  getDataSource: (id) => api.get(`/data-sources/${id}`),
  getDataSourceSchema: (id) => api.get(`/data-sources/${id}/schema`),
  getDataSourceData: (id, params) => api.get(`/data-sources/${id}/data`, { params }),
  createDataSource: (data) => api.post('/data-sources', data),
  uploadFile: (formData) => api.post('/data-sources/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  testConnection: (id) => api.post(`/data-sources/${id}/test`),
  importData: (id, mode) => api.post(`/data-sources/${id}/import`, { mode }),
  getImportStatus: (importId) => api.get(`/data-sources/imports/${importId}/status`),
  updateSchedule: (id, schedule) => api.patch(`/data-sources/${id}/schedule`, { schedule }),
  deduplicateData: (id, keyColumns) => api.post(`/data-sources/${id}/deduplicate`, { keyColumns }),
  generateDataSourceAIContext: (id) => api.post(`/data-sources/${id}/generate-ai-context`, {}, { timeout: 120000 }), // 2 minutes for AI
  updateDataSource: (id, data) => api.put(`/data-sources/${id}`, data),
  deleteDataSource: (id) => api.delete(`/data-sources/${id}`),

  // Web Scraper
  estimateScrape: (data) => api.post('/web-scraper/estimate', data),
  previewScrape: (data) => api.post('/web-scraper/preview', data, { timeout: 120000 }),
  executeScrape: (data) => api.post('/web-scraper/scrape', data, { timeout: 300000 }),

  // AI Research (Data Generation)
  previewAiResearch: (data) => api.post('/ai-research/preview', data, { timeout: 120000 }),
  generateAiResearch: (data) => api.post('/ai-research/generate', data, { timeout: 120000 }),
  
  // AI
  getModels: () => api.get('/ai/models'),
  chat: (data) => api.post('/ai/chat', data, { timeout: 120000 }), // 2 minutes for AI with tool calls
  
  // Streaming chat with progress updates (SSE)
  chatStream: async (data, onProgress) => {
    // Get auth token
    let token = null;
    const authData = localStorage.getItem('neoshift-auth');
    if (authData) {
      try {
        const { state } = JSON.parse(authData);
        token = state?.accessToken;
      } catch (e) {
        console.error('Error parsing auth data:', e);
      }
    }
    
    const response = await fetch(`${API_URL}/ai/chat/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
      body: JSON.stringify(data)
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let result = null;
    
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      
      buffer += decoder.decode(value, { stream: true });
      
      // Process complete SSE messages (separated by double newlines)
      const lines = buffer.split('\n\n');
      buffer = lines.pop() || ''; // Keep incomplete data in buffer
      
      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6));
            
            if (data.type === 'progress' && onProgress) {
              onProgress(data.data);
            } else if (data.type === 'complete') {
              result = data.data;
            } else if (data.type === 'error') {
              throw new Error(data.error || 'Unknown error');
            }
          } catch (e) {
            if (e.message !== 'Unknown error' && !e.message.includes('JSON')) {
              throw e;
            }
            console.error('Error parsing SSE data:', e);
          }
        }
      }
    }
    
    return { data: result };
  },

  // Streaming chart generation with progress (SSE, 15 correction attempts)
  generateChartWithAIStream: async (data, onProgress) => {
    let token = null;
    const authData = localStorage.getItem('neoshift-auth');
    if (authData) {
      try {
        const { state } = JSON.parse(authData);
        token = state?.accessToken;
      } catch (e) {
        console.error('Error parsing auth data:', e);
      }
    }

    const response = await fetch(`${API_URL}/ai/generate-chart/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let result = null;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const parsed = JSON.parse(line.slice(6));
            if (parsed.type === 'progress' && onProgress) {
              onProgress(parsed.data);
            } else if (parsed.type === 'complete') {
              result = parsed.data;
            } else if (parsed.type === 'error') {
              throw new Error(parsed.error || 'Unknown error');
            }
          } catch (e) {
            if (e instanceof Error && e.message !== 'Unknown error' && !e.message.includes('JSON')) {
              throw e;
            }
            console.error('Error parsing SSE data:', e);
          }
        }
      }
    }

    return { data: result };
  },

  generateChartWithAI: (data) => api.post('/ai/generate-chart', data, { timeout: 120000 }), // AI chart builder
  explainSQLQuery: (data) => api.post('/ai/explain-sql', data, { timeout: 60000 }),
  getConversations: (params) => api.get('/ai/conversations', { params }),
  getConversation: (id) => api.get(`/ai/conversations/${id}`),
  deleteConversation: (id) => api.delete(`/ai/conversations/${id}`),
  
  // Users
  getUsers: (params) => api.get('/users', { params }),
  getUsersByCompany: (companyId, params) => api.get(`/users/company/${companyId}`, { params }),
  getUser: (id) => api.get(`/users/${id}`),
  createUser: (data) => api.post('/users', data),
  updateUser: (id, data) => api.put(`/users/${id}`, data),
  updateUserPermissions: (id, data) => api.patch(`/users/${id}/permissions`, data),
  deleteUser: (id) => api.delete(`/users/${id}`),
  resendVerificationEmail: (id) => api.post(`/users/${id}/resend-verification`),
  updateOnboarding: (data) => api.patch('/users/me/onboarding', data),
  
  // Companies
  getCompanies: (params) => api.get('/companies', { params }),
  getCompany: (id) => api.get(`/companies/${id}`),
  getCompanyStats: (id) => api.get(`/companies/${id}/stats`),
  updateCompany: (id, data) => api.put(`/companies/${id}`, data),
  createCompany: (data) => api.post('/companies', data),
  
  // Regions
  getAvailableRegions: () => api.get('/regions/available'),
  
  // Billing - accepts companyId for Super/Managing Admins
  getUsage: (companyId) => api.get('/billing/usage', {
    params: companyId ? { companyId } : {}
  }),
  getStorageUsage: (companyId) => api.get('/billing/storage', {
    params: companyId ? { companyId } : {}
  }),
  getAITokenUsage: (companyId) => api.get('/billing/ai-tokens', {
    params: companyId ? { companyId } : {}
  }),
  getSeatCount: (companyId) => api.get('/billing/seats', {
    params: companyId ? { companyId } : {}
  }),
  
  // Subscription & Credits
  getSubscriptionPlans: () => api.get('/billing/plans'),
  getCreditBalance: (companyId) => api.get('/billing/credits', { 
    params: companyId ? { companyId } : {} 
  }),
  getCreditPacks: () => api.get('/billing/credit-packs'),
  
  // Beta Pricing
  getBetaStatus: (companyId) => api.get(`/billing/beta-status/${companyId}`),
  getBetaPricing: (planSlug, companyId, currencyCode) => api.get(`/billing/beta-pricing/${planSlug}`, {
    params: { 
      ...(companyId ? { companyId } : {}),
      ...(currencyCode ? { currencyCode } : {})
    }
  }),
  getCreditTransactions: (params) => api.get('/billing/credit-transactions', { params }),
  getAIFeatureCosts: () => api.get('/billing/ai-feature-costs'),
  getCurrencies: () => api.get('/billing/currencies'),
  
  // Enterprise Inquiries (public)
  submitEnterpriseInquiry: (data) => api.post('/enterprise/inquiries', data),
  getEnterpriseInquiryStatus: (id, email) => api.get(`/enterprise/inquiries/${id}/status`, { params: { email } }),
  addEnterpriseInquiryMessage: (id, data) => api.post(`/enterprise/inquiries/${id}/messages`, data),
  
  // Additional Seats Pricing
  getAdditionalSeatsPricing: () => api.get('/billing/additional-seats-pricing'),
  
  // Lemon Squeezy Checkout
  getLemonSqueezyConfig: () => api.get('/billing/lemon-squeezy/config'),
  getSubscriptionStatus: (companyId) => api.get('/billing/subscription/status', {
    params: companyId ? { companyId } : {}
  }),
  createSubscriptionCheckout: (data) => api.post('/billing/checkout/subscription', data),
  createCreditPackCheckout: (data) => api.post('/billing/checkout/credits', data),
  createSeatsCheckout: (data) => api.post('/billing/checkout/seats', data),
  cancelSubscription: (data) => api.post('/billing/subscription/cancel', data),
  
  // Lemon Squeezy Transaction History & Invoices
  getTransactionHistory: (params) => api.get('/billing/transactions', { params }),
  getSubscriptionInvoices: (params) => api.get('/billing/subscription/invoices', { params }),
  
  // Warehouse Storage
  getWarehouseStorage: (companyId) => api.get('/warehouse/storage', { 
    params: companyId ? { companyId } : {} 
  }),
  
  // Marts (dbt Business Logic)
  getMarts: (params) => api.get('/marts', { params }),
  getMart: (id) => api.get(`/marts/${id}`),
  getMartSources: (params) => api.get('/marts/sources', { params }),
  createMart: (data) => api.post('/marts', data),
  updateMart: (id, data) => api.put(`/marts/${id}`, data),
  generateMart: (id) => api.post(`/marts/${id}/generate`),
  generateAllMarts: (params) => api.post('/marts/generate-all', {}, { params }),
  previewMartSql: (data) => api.post('/marts/preview-sql', data),
  previewMartData: (data, limit = 100) => api.post(`/marts/preview-data?limit=${limit}`, data),
  duplicateMart: (id, data) => api.post(`/marts/${id}/duplicate`, data),
  deleteMart: (id) => api.delete(`/marts/${id}`),
  generateMartWithAI: (data) => api.post('/marts/ai-generate', data),
  editMartWithAI: (data) => api.post('/marts/ai-edit', data, { timeout: 120000 }),
  
  // Metrics (Semantic Layer)
  generateMetricWithAI: (data) => api.post('/metrics/ai-generate', data),
  
  // Data Studio
  getDataStudioOverview: (params) => api.get('/data-studio/overview', { params }),
  getDataStudioSuggestions: (params) => api.get('/data-studio/suggestions', { params }),
  dataStudioAIAssist: (data) => api.post('/data-studio/ai-assist', data, { timeout: 120000 }),
  dataStudioAutoSetup: (data) => api.post('/data-studio/auto-setup', data),
  
  // Relationships
  getRelationships: (params) => api.get('/relationships', { params }),
  
  // Questions (Question Builder)
  getQuestions: (params) => api.get('/questions', { params }),
  getQuestion: (id) => api.get(`/questions/${id}`),
  createQuestion: (data) => api.post('/questions', data),
  updateQuestion: (id, data) => api.put(`/questions/${id}`, data),
  deleteQuestion: (id) => api.delete(`/questions/${id}`),
  previewQuestion: (data) => api.post('/questions/preview', data, { timeout: 60000 }),
  runQuestion: (id, data) => api.post(`/questions/${id}/run`, data, { timeout: 60000 }),
  publishQuestionAsMart: (id, data) => api.post(`/questions/${id}/publish-as-mart`, data),
  getRelatedTables: (tableName, params) => api.get(`/questions/related-tables/${tableName}`, { params }),
  validateJoins: (data) => api.post('/questions/validate-joins', data),
  getQuestionFolders: (params) => api.get('/questions/folders', { params }),
  
  // Help / Documentation
  getHelpDocs: (params) => api.get('/help/docs', { params }),
  getHelpDoc: (slug) => api.get(`/help/docs/${slug}`),
  searchHelpDocs: (q, category) => api.get('/help/docs/search', { params: { q, category } }),
  askHelpQuestion: (question) => api.post('/help/ask', { question }),
  recordHelpFeedback: (slug, isHelpful) => api.post(`/help/docs/${slug}/feedback`, { isHelpful }),
  
  // Tickets
  getTickets: (params) => api.get('/tickets', { params }),
  getTicket: (id) => api.get(`/tickets/${id}`),
  createTicket: (data) => api.post('/tickets', data),
  updateTicket: (id, data) => api.patch(`/tickets/${id}`, data),
  addTicketMessage: (id, content) => api.post(`/tickets/${id}/messages`, { content }),
  closeTicket: (id) => api.post(`/tickets/${id}/close`),
  getUnreadTicketCount: () => api.get('/tickets/unread-count'),
  
  // SDK Integration
  getSDKConfig: (companyId) => api.get(`/sdk/admin/${companyId}`),
  getSDKUsage: (companyId, params) => api.get(`/sdk/admin/${companyId}/usage`, { params }),
  testSDKConfig: (companyId, data) => api.post(`/sdk/admin/${companyId}/test`, data),
  getSDKInfo: () => api.get('/sdk/admin/info'),
  
  // Referrals
  getReferralCode: () => api.get('/referrals/my-code'),
  validateReferralCode: (code) => api.post('/referrals/validate', { code }),
  applyReferralCode: (code) => api.post('/referrals/apply', { code }),
  getReferralStats: () => api.get('/referrals/stats'),
  getReferralHistory: (params) => api.get('/referrals/history', { params }),
  getReferralInfo: () => api.get('/referrals/info'),
  
  // Admin Referrals
  getAllReferrals: (params) => api.get('/referrals/admin/all', { params }),
  getGlobalReferralStats: (params) => api.get('/referrals/admin/stats', { params }),
  getReferralDetails: (id) => api.get(`/referrals/admin/${id}`),
  blockReferral: (id, reason) => api.post(`/referrals/admin/${id}/block`, { reason }),
  
  // Mailbox Contacts (public)
  submitMailboxContact: (mailboxType, data) => api.post(`/mailbox/${mailboxType}`, data),
  // Backward compatibility
  submitSalesContact: (data) => api.post('/mailbox/sales', data),
  
  // Insight Blocks
  createInsightBlock: async (data, onProgress) => {
    let token = null;
    const authData = localStorage.getItem('neoshift-auth');
    if (authData) {
      try {
        const { state } = JSON.parse(authData);
        token = state?.accessToken;
      } catch (e) { /* ignore */ }
    }

    const response = await fetch(`${API_URL}/insight-blocks`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let result = null;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const parsed = JSON.parse(line.slice(6));
            if (parsed.type === 'progress' && onProgress) {
              onProgress(parsed.data);
            } else if (parsed.type === 'complete') {
              result = parsed.data;
            } else if (parsed.type === 'error') {
              throw new Error(parsed.error || 'Unknown error');
            }
          } catch (e) {
            if (e instanceof Error && e.message !== 'Unknown error' && !e.message.includes('JSON')) {
              throw e;
            }
          }
        }
      }
    }

    return { data: result };
  },

  refreshInsightBlock: (id) => api.post(`/insight-blocks/${id}/refresh`),

  editInsightBlockSimple: (id, data) => api.put(`/insight-blocks/${id}`, data),

  editInsightBlockStream: async (id, data, onProgress) => {
    let token = null;
    const authData = localStorage.getItem('neoshift-auth');
    if (authData) {
      try {
        const { state } = JSON.parse(authData);
        token = state?.accessToken;
      } catch (e) { /* ignore */ }
    }

    const response = await fetch(`${API_URL}/insight-blocks/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    const contentType = response.headers.get('content-type');

    // If not SSE, it's a simple JSON response (schedule-only change)
    if (contentType && contentType.includes('application/json')) {
      const json = await response.json();
      return { data: json };
    }

    // SSE stream for regeneration
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let result = null;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const parsed = JSON.parse(line.slice(6));
            if (parsed.type === 'progress' && onProgress) {
              onProgress(parsed.data);
            } else if (parsed.type === 'complete') {
              result = parsed.data;
            } else if (parsed.type === 'error') {
              throw new Error(parsed.error || 'Unknown error');
            }
          } catch (e) {
            if (e instanceof Error && e.message !== 'Unknown error' && !e.message.includes('JSON')) {
              throw e;
            }
          }
        }
      }
    }

    return { data: result };
  },

  deleteInsightBlock: (id) => api.delete(`/insight-blocks/${id}`),

  getInsightBlock: (id) => api.get(`/insight-blocks/${id}`),

  // Image Blocks
  createImageBlock: (formData) => api.post('/image-blocks', formData),
  updateImageBlock: (id, data) => api.put(`/image-blocks/${id}`, data),
  deleteImageBlock: (id) => api.delete(`/image-blocks/${id}`),

  // Text Blocks
  createTextBlock: (data) => api.post('/text-blocks', data),
  updateTextBlock: (id, data) => api.put(`/text-blocks/${id}`, data),
  deleteTextBlock: (id) => api.delete(`/text-blocks/${id}`)
};

